import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { intentAPI } from '../../services/(adminapi)/intentAPI'

// Async thunks
export const fetchIntents = createAsyncThunk(
  'intent/fetchIntents',
  async (_, { rejectWithValue }) => {
    try {
      const response = await intentAPI.getIntents()
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch intents')
    }
  }
)

export const createIntent = createAsyncThunk(
  'intent/createIntent',
  async (intentData, { rejectWithValue }) => {
    try {
      const response = await intentAPI.createIntent(intentData)
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to create intent')
    }
  }
)

export const updateIntent = createAsyncThunk(
  'intent/updateIntent',
  async ({ id, intentData }, { rejectWithValue }) => {
    try {
      const response = await intentAPI.updateIntent(id, intentData)
      return { id, data: response.data }
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to update intent')
    }
  }
)

export const deleteIntent = createAsyncThunk(
  'intent/deleteIntent',
  async (id, { rejectWithValue }) => {
    try {
      const response = await intentAPI.deleteIntent(id)
      return { id, response }
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to delete intent')
    }
  }
)

const initialState = {
  intents: [],
  loading: false,
  error: null,
  success: null
}

const intentSlice = createSlice({
  name: 'intent',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null
    },
    clearSuccess: (state) => {
      state.success = null
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch intents
      .addCase(fetchIntents.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchIntents.fulfilled, (state, action) => {
        state.loading = false
        if (action.payload.success) {
          state.intents = action.payload.data || []
        }
        state.error = null
      })
      .addCase(fetchIntents.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
        state.intents = []
      })
      
      // Create intent
      .addCase(createIntent.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(createIntent.fulfilled, (state, action) => {
        state.loading = false
        if (action.payload.success && action.payload.data) {
          state.intents.push(action.payload.data)
          state.success = 'Intent created successfully'
        }
        state.error = null
      })
      .addCase(createIntent.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      
      // Update intent
      .addCase(updateIntent.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(updateIntent.fulfilled, (state, action) => {
        state.loading = false
        const index = state.intents.findIndex(intent => intent._id === action.payload.id)
        if (index !== -1 && action.payload.data?.data) {
          state.intents[index] = action.payload.data.data
        }
        state.success = 'Intent updated successfully'
        state.error = null
      })
      .addCase(updateIntent.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      
      // Delete intent
      .addCase(deleteIntent.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(deleteIntent.fulfilled, (state, action) => {
        state.loading = false
        state.intents = state.intents.filter(intent => intent._id !== action.payload.id)
        state.success = 'Intent deleted successfully'
      })
      .addCase(deleteIntent.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
  }
})

export const { clearError, clearSuccess } = intentSlice.actions
export default intentSlice.reducer

